import { useState } from 'react';
import type { Permission } from '@abastece/shared';
import { usePuede } from '../lib/sesion.js';
import { enPractica, entrarAPractica, salirDePractica } from '../lib/practica.js';
import { Pagina } from '../ui/Pagina.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { Confirmar } from '../ui/Preguntar.js';
import { useT } from '../i18n/index.js';
import { clavePara, claveTitulo, leccionesPara, pasosPara, pasosQueSeVen } from '../tutorial/lecciones.js';
import { leccionesHechas, marcarOfrecido, olvidarProgreso } from '../tutorial/motor.js';
import { iniciarLeccion } from '../tutorial/Tutorial.js';

/**
 * El índice de lecciones.
 *
 * Two different screens depending on where you are standing. In the real shop it is an offer:
 * here is what there is to learn, and the button takes you to a practice shop to learn it in.
 * Inside the practice shop it is the menu you actually work from, with a way out that throws
 * the sandbox away.
 *
 * Lessons are listed in work order and never hidden once done — somebody who has forgotten how
 * to settle a route wants to find that lesson, not be told they already did it. The tick is
 * information, not a lock.
 */
export function Aprender() {
  const t = useT();
  const puede = usePermisos();
  const practica = enPractica();
  const lecciones = leccionesPara(puede);
  const hechas = leccionesHechas();
  const [saliendo, setSaliendo] = useState(false);
  const [trabajando, setTrabajando] = useState(false);

  const pendientes = lecciones.filter((l) => !hechas.has(l.id));
  const siguiente = pendientes[0] ?? lecciones[0];

  async function entrar() {
    setTrabajando(true);
    marcarOfrecido();
    try {
      await entrarAPractica();
    } catch {
      setTrabajando(false);
    }
  }

  return (
    <Pagina
      titulo={t('aprender.titulo')}
      subtitulo={t('aprender.hechas', { n: lecciones.length - pendientes.length, total: lecciones.length })}
    >
      {practica ? (
        <div className="tarjeta mb-5 ring-violet-500/30">
          <div className="flex items-start gap-3">
            <Chip icono="listo" tinte="violeta" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-violet-200">{t('aprender.enPractica')}</p>
              <p className="mt-1 text-sm text-slate-400">{t('aprender.enPracticaDetalle')}</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-secundario mt-4 w-full"
            onClick={() => setSaliendo(true)}
          >
            <Ico icono="salir" />
            {t('aprender.salirPractica')}
          </button>
        </div>
      ) : (
        <div className="tarjeta mb-5">
          <p className="text-sm leading-relaxed text-slate-300">{t('aprender.invitacion')}</p>
          <button
            type="button"
            className="hero mt-4"
            disabled={trabajando}
            onClick={() => void entrar()}
          >
            <Ico icono="aprender" className="size-7" />
            {t(trabajando ? 'aprender.abriendo' : 'aprender.empezar')}
          </button>
          <p className="mt-3 text-center text-xs text-slate-500">{t('aprender.nadaReal')}</p>
        </div>
      )}

      <h2 className="rotulo">{t('aprender.lecciones')}</h2>
      <ul className="flex flex-col gap-2">
        {lecciones.map((l) => {
          const hecha = hechas.has(l.id);
          const esSiguiente = practica && l.id === siguiente?.id;
          return (
            <li key={l.id}>
              <button
                type="button"
                disabled={!practica}
                onClick={() => iniciarLeccion(l.id)}
                className={`tarjeta-accion flex w-full items-center gap-4 p-3.5 text-left disabled:opacity-60 ${
                  esSiguiente ? 'ring-marca-500/40' : ''
                }`}
              >
                <Chip icono={l.icono} tinte={l.tinte} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{t(claveTitulo(l.id))}</span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                    {t(clavePara(l.id))}
                  </span>
                </span>
                {hecha ? (
                  <Ico icono="listo" className="size-5 shrink-0 text-emerald-400" />
                ) : (
                  <span className="pastilla shrink-0 bg-slate-800 text-slate-400">
                    {t('aprender.pasos', { n: pasosQueSeVen(pasosPara(l, puede)) })}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {hechas.size > 0 && (
        <button
          type="button"
          className="btn-texto mt-6 gap-1 text-xs text-slate-500"
          onClick={() => {
            olvidarProgreso();
            location.reload();
          }}
        >
          <Ico icono="reiniciar" className="size-3.5" />
          {t('aprender.olvidar')}
        </button>
      )}

      {saliendo && (
        <Confirmar
          titulo={t('aprender.salirTitulo')}
          mensaje={t('aprender.salirMensaje')}
          confirmar={t('aprender.salirConfirmar')}
          icono="salir"
          onConfirmar={() => void salirDePractica()}
          onCerrar={() => setSaliendo(false)}
        />
      )}
    </Pagina>
  );
}

/**
 * Every permission any lesson asks about, resolved up front.
 *
 * Same reason as the menu: `usePuede` is a hook, so it cannot be called from inside a filter
 * whose length changes with the role.
 */
function usePermisos(): (p: Permission) => boolean {
  const mapa: Partial<Record<Permission, boolean>> = {
    vender: usePuede('vender'),
    anular_venta: usePuede('anular_venta'),
    abrir_caja: usePuede('abrir_caja'),
    cerrar_caja: usePuede('cerrar_caja'),
    editar_productos: usePuede('editar_productos'),
    editar_precios: usePuede('editar_precios'),
    recibir_compras: usePuede('recibir_compras'),
    ajustar_inventario: usePuede('ajustar_inventario'),
    gestionar_clientes: usePuede('gestionar_clientes'),
    ver_reportes: usePuede('ver_reportes'),
    gestionar_planilla: usePuede('gestionar_planilla'),
    // A lesson's steps about costs (`Paso.permisos`), which a cashier's screens do not show.
    ver_costos: usePuede('ver_costos'),
  };
  return (p) => mapa[p] === true;
}
